import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { EContentGreeting, IContent, IContentFindResponse, IContentItem } from 'app/store/models/content.model';
import { ISelect, ISelectItem } from 'app/store/models/select.model';
import { ContentService } from 'app/store/services/content.service';
import { IContentGroup } from 'app/store/models/content-group.model';
import { SessionService } from 'app/store/services/session.service';
import { UserService } from 'app/store/services/user.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FLASH_TYPE, IUserSessionData } from 'app/store/models/user.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ECampaignGoal, ECampaignStateStatus, ICampaign, ICampaignState } from 'app/store/models/campaigns.model';
import { Store } from '@ngrx/store';
import { EReducers, IReducers } from 'app/store/reducers';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { InsertHyperlinkComponent } from 'app/dialogs/insert-hyperlink/insert-hyperlink.component';
import { DialogComponent } from 'app/shared/ui/dialog/dialog.component';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { updateCampaign } from 'app/store/actions/campaigns.action';
import { setFlash } from 'app/store/actions/user.action';
import { InfoTipsComponent } from '../../dialogs/info-tips/info-tips.component';
import { CampaignService } from '../../store/services/campaign.service';

@Component({
  selector: 'app-communication-content',
  templateUrl: './communication-content.component.html',
  styleUrls: ['./communication-content.component.scss'],
})
export class CommunicationContentComponent implements OnInit, OnDestroy {
  @ViewChild('editor', { read: EditorComponent }) editor: EditorComponent;

  @ViewChild('attention', { read: EditorComponent }) attention: EditorComponent;
  @ViewChild('benefit1', { read: EditorComponent }) benefit1: EditorComponent;
  @ViewChild('benefit2', { read: EditorComponent }) benefit2: EditorComponent;
  @ViewChild('details', { read: EditorComponent }) details: EditorComponent;

  contentItems: ISelectItem[] = [
    {
      label: 'Empty',
      value: null,
    },
    {
      label: 'Enter Content Code',
      value: 'enter_content_code',
    },
  ];

  selectContentProperties: ISelect<IContentItem> = {
    label: 'Select Existing Content',
  };

  greetingsSelect: ISelect<any> = {
    label: 'Select greeting',
    items: [
      {
        label: EContentGreeting.HI_FIRST_NAME,
        value: EContentGreeting.HI_FIRST_NAME,
      },
      {
        label: EContentGreeting.FIRST_NAME,
        value: EContentGreeting.FIRST_NAME,
      },
      {
        label: EContentGreeting.ALOHA,
        value: EContentGreeting.ALOHA,
      },
      {
        label: EContentGreeting.NEW,
        value: 'new',
      },
    ],
  };
  backButton: Button = {
    color: ButtonColor.BLUE,
    route: ['/campaigns/new'],
    size: ButtonSize.SMALL,
    text: 'Back',
    style: 'outline',
  };
  previewButton: Button = {
    color: ButtonColor.ORANGE,
    size: ButtonSize.SMALL,
    text: 'Preview',
    onClick: () => this.showConfirmDialog(),
    style: 'filled',
  };
  formGroup: FormGroup;
  componentReady: boolean;
  showCustomGreeting: boolean;
  backToGreetingsButton: Button = {
    text: 'Back to greetings',
    color: ButtonColor.WHITE,
    size: ButtonSize.SMALL,
    onClick: () => (this.showCustomGreeting = false),
  };
  showContentCodeControl: boolean;

  variables = [];

  campaignId: number;
  selection = null;
  validationError: boolean;
  campaignInfo: ICampaign;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private editMode: boolean;
  private contentFilledFromCustom: boolean;
  private savedSubject: string;
  private selectedContentId: number;

  constructor(
    private contentService: ContentService,
    private sessionService: SessionService,
    private userService: UserService,
    private store: Store<IReducers>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private readonly campaignService: CampaignService,
  ) {
  }

  get attention_control() {
    return this.formGroup.get('attention');
  }

  get goal_control() {
    return this.formGroup.get('goal');
  }

  get greeting_control() {
    return this.formGroup.get('greeting');
  }

  get custom_greeting_control() {
    return this.formGroup.get('custom_greeting');
  }

  get q_answer_control() {
    return this.formGroup.get('q_answer');
  }

  get benefit_1_control() {
    return this.formGroup.get('benefit_1');
  }

  get benefit_2_control() {
    return this.formGroup.get('benefit_2');
  }

  get details_control() {
    return this.formGroup.get('details');
  }

  get signature_control() {
    return this.formGroup.get('signature');
  }

  onGreetingsSelection = value => {
    if (value === 'new') {
      this.formGroup.get('custom_greeting').setValue('');
      this.showCustomGreeting = true;
    } else {
      this.formGroup.get('custom_greeting').setValue(value);
    }
  };

  onContentSelect(contentId: number | string) {
    if (contentId === 'enter_content_code') {
      this.showContentCodeControl = true;
      return;
    }
    this.showContentCodeControl = false;
    if (contentId) {
      this.contentService
        .getGroupContents(this.campaignId, +contentId)
        .subscribe((contents: IContent[]) => {
          if (contents && contents.length > 0) {
            this.fillContents(contents[0]);
          }
        });
    } else {
      this.enableAllControls();
    }
  }

  enableAllControls() {
    this.goal_control.enable();
    this.q_answer_control.enable();
    this.attention_control.enable();
    this.benefit_1_control.enable();
    this.benefit_2_control.enable();
    this.details_control.enable();
    this.signature_control.enable();
    this.greeting_control.enable();
    this.custom_greeting_control.enable();
  }

  disableAllControls() {
    this.goal_control.disable();
    this.q_answer_control.disable();
    this.attention_control.disable();
    this.benefit_1_control.disable();
    this.benefit_2_control.disable();
    this.details_control.disable();
    this.signature_control.disable();
    this.greeting_control.disable();
    this.custom_greeting_control.disable();
  }

  showConfirmDialog() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.validationError = true;
      window.scrollTo({
        top: 0,
      });
      return false;
    }
    this.store.dispatch(
      openDialog({
        dialogData: {
          canClose: false,
          content: `Retreva&reg; will now walk you through approving 4 emails. These emails will auto generate your opening and closing statements that drive towards your goal (phone call or meeting). Your statements will automatically be placed in the emails. You need to edit and approve each of the 4 emails. These emails will continue to rotate through the life of the campaign. So once email 4 is sent, email 5 will be email template 1. You can edit your templates at any time by clicking on Content and Content Group.`,
          title: 'Campaign Emails',
          buttons: [
            {
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.SMALL,
              text: 'Ok',
              onClick: () => this.updateCampaign(),
            },
          ],
        },
      }),
    );
  }

  updateCampaign() {
    const data: any = {
      campaign_id: this.campaignId,
      campaign_attention: this.attention_control.value,
      campaign_details: this.details_control.value,
      campaign_goal: this.goal_control.value,
      campaign_greeting: this.showCustomGreeting
        ? this.custom_greeting_control.value + ' {first_name},'
        : this.greeting_control.value,
      campaign_sign: this.signature_control.value,
      first_benefit: this.benefit_1_control.value,
      second_benefit: this.benefit_2_control.value,
      quick_answer: this.q_answer_control.value,
      campaign_group_id: this.formGroup.get('content_group_id').value,
    };

    if (this.selectedContentId) {
      data.content_group_id = this.selectedContentId;
    }

    if (this.contentFilledFromCustom) {
      data.subject = this.savedSubject;
    }
    this.store.dispatch(
      updateCampaign({
        campaign: data,
      }),
    );
  }

  fillContentsFromSearch(content: IContentFindResponse, contentCodeName: string) {
    if (content.campaign_goal) {
      this.goal_control.setValue(content.campaign_goal);
    }

    if (content.quick_answer) {
      this.q_answer_control.setValue(content.quick_answer);
    }

    if (content.content_attention) {
      this.attention_control.setValue(content.content_attention);
    }

    if (content.first_benefit) {
      this.benefit_1_control.setValue(content.first_benefit);
    }

    if (content.second_benefit) {
      this.benefit_2_control.setValue(content.second_benefit);
    }

    if (content.content_details) {
      this.details_control.setValue(content.content_details);
    }

    if (content.content_sign) {
      this.details_control.setValue(content.content_sign);
    }

    if (content.greeting) {
      if (
        [
          EContentGreeting.FIRST_NAME.toString(),
          EContentGreeting.HI_FIRST_NAME.toString(),
          EContentGreeting.ALOHA.toString(),
        ].indexOf(content.greeting) === -1
      ) {
        this.showCustomGreeting = true;
        this.custom_greeting_control.setValue(content.greeting);
      } else {
        this.greeting_control.setValue(content.greeting);
      }
    }

    if (content.content_group_id) {
      this.selectedContentId = +content.content_group_id;
    }

    this.contentFilledFromCustom = true;
    this.savedSubject = content.subject;

    this.store.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: `Content ${contentCodeName} successfully loaded.`,
          type: 'success',
        },
      },
    }));
  }

  fillContents(content: IContent) {
    if (content.content_goal) {
      this.goal_control.setValue(content.content_goal);
    }

    if (content.quick_answer) {
      this.q_answer_control.setValue(content.quick_answer);
    }

    if (content.content_attention) {
      this.attention_control.setValue(content.content_attention);
    }

    if (content.first_benefit) {
      this.benefit_1_control.setValue(content.first_benefit);
    }

    if (content.second_benefit) {
      this.benefit_2_control.setValue(content.second_benefit);
    }

    if (content.content_details) {
      this.details_control.setValue(content.content_details);
    }

    if (content.content_sign) {
      this.signature_control.setValue(content.content_sign);
    }

    if (content.greeting) {
      if (
        [
          EContentGreeting.FIRST_NAME,
          EContentGreeting.HI_FIRST_NAME,
          EContentGreeting.ALOHA,
        ].indexOf(content.greeting) === -1
      ) {
        this.showCustomGreeting = true;
        this.custom_greeting_control.setValue(content.greeting);
      } else {
        this.greeting_control.setValue(content.greeting);
      }
    }

    this.selectedContentId = content.content_group_id;

    this.store.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: `Content code ${content.campaign_name} successfully loaded`,
          type: 'success',
        },
      },
    }));
  }

  ngOnInit() {
    this.campaignId = this.activatedRoute.snapshot.params.id;
    this.editMode = !!this.activatedRoute.snapshot.queryParams.edit;
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sessionData: IUserSessionData) => {
        if (this.campaignId) {
          this.campaignService.getCampaignInfo(this.campaignId)
            .subscribe(campaign => this.campaignInfo = campaign);
        }
        if (this.campaignId && this.editMode) {
          this.contentService
            .getCampaignInfo(this.campaignId)
            .subscribe(data => {
              if (data['0'] && data['0'].content_group_id) {
                this.selection = data['0'].content_group_id;
                this.fillContents(data[0]);
              }
            });
        }
        this.formGroup = new FormGroup({
          content_code: new FormControl(''),
          content_group_id: new FormControl(null),
          goal: new FormControl(ECampaignGoal.PHONE),
          greeting: new FormControl(EContentGreeting.HI_FIRST_NAME),
          custom_greeting: new FormControl(),
          q_answer: new FormControl('', [Validators.required]),
          attention: new FormControl('', [Validators.required]),
          benefit_1: new FormControl('', [Validators.required]),
          benefit_2: new FormControl('', [Validators.required]),
          details: new FormControl(''),
          signature: new FormControl(sessionData.signature),
        });

        this.userService.getLabels().subscribe(response => {
          const vars = [];
          vars.push({ label: '------', value: '' });
          if (response) {
            if (response.label1 !== '') {
              vars.push({
                label: response.label1,
                value: response.label1,
              });
            }

            if (response.label2 !== '') {
              vars.push({
                label: response.label2,
                value: response.label2,
              });
            }

            if (response.label3 !== '') {
              vars.push({
                label: response.label3,
                value: response.label3,
              });
            }

            if (vars.length > 1) {
              this.variables = vars;
            }
            this.componentReady = true;
          } else {
            this.componentReady = true;
          }
        });
        this.contentService
          .getAllContentGroups(sessionData.id)
          .subscribe((contentGroups: IContentGroup[]) => {
            if (contentGroups && contentGroups.length > 0) {
              const newContentItems = [];
              for (const contentGroup of contentGroups) {
                newContentItems.push({
                  label: contentGroup.content_group,
                  value: contentGroup.content_group_id,
                });
              }

              if (newContentItems.length) {
                this.contentItems = [...this.contentItems, ...newContentItems];
              }
            }
          });
      });

    this.store
      .select(EReducers.CAMPAIGNS)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: ICampaignState) => {
        if (
          state.status === ECampaignStateStatus.UPDATED &&
          state.update_response.campaign_id
        ) {
          this.router.navigate([
            '/campaigns',
            state.update_response.campaign_id,
            'preview-content',
          ]);
        }
        if (state.status === ECampaignStateStatus.ERROR) {
          let msg = '';
          if (state.error.message) {
            if (Array.isArray(state.error.message)) {
              state.error.message.forEach(item => {
                msg = msg.concat(item);
              });
            } else {
              msg = state.error.message;
            }
          } else {
            msg = 'Something went wrong!';
          }
          this.store.dispatch(
            setFlash({
              data: {
                type: FLASH_TYPE.ALERT,
                alertMessage: msg,
              },
            }),
          );

          window.scrollTo(0, 0);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
    this.destroy$.complete();
  }

  insertHyperlink(controlName: string) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InsertHyperlinkComponent,
          title: 'Insert Hyperlink',
          canClose: true,
          dialogMinWidth: '700px',
          buttons: [
            {
              color: ButtonColor.BLUE,
              size: ButtonSize.SMALL,
              text: 'Close',
              actAsClose: true,
            },
            {
              color: ButtonColor.BLUE,
              size: ButtonSize.SMALL,
              text: 'Insert Link',
              actAsClose: true,
              onClick: (dialog: DialogComponent) => {
                if (dialog.dialogForm.valid) {
                  const anchor = `<a href='${
                    dialog.dialogForm.get('url').value
                  }' target='${dialog.dialogForm.get('target').value}'>${
                    dialog.dialogForm.get('content').value
                  }</a>`;
                  this.insertLink(anchor, controlName);
                }
              },
            },
          ],
        },
      }),
    );
  }

  getErrorMessage() {
    if (this.signature_control.hasError('required')) {
      return 'The field is required';
    }
    return 'field is required';
  }

  onVariableSelect(event, control) {
    switch (control) {
      case 'details':
        this.details.editor.insertContent(event);
        break;
      case 'benefit_1':
        this.benefit1.editor.insertContent(event);
        break;
      case 'benefit_2':
        this.benefit2.editor.insertContent(event);
        break;
      case 'statement':
        this.attention.editor.insertContent(event);
        break;
    }
  }

  openExamplesDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type: 'example_attention',
          },
        },
      }),
    );
  }

  searchContentCode() {
    if (!this.formGroup.get('content_code').value) {
      return;
    }
    this.contentService
      .findContentByCode(this.formGroup.get('content_code').value)
      .subscribe(result => {
        if (result) {
          if (Array.isArray(result) && result.length === 0) {
            this.store.dispatch(openDialog({
              dialogData: {
                title: 'Alert',
                content: `Content code ${this.formGroup.get('content_code').value} not found`,
                canClose: true,
              },
            }));
            return;
          }
          this.fillContentsFromSearch(result, this.formGroup.get('content_code').value);
        }
      });
  }

  private insertLink(linkContent: string, controlName: string) {
    const elementRef: ElementRef = this[controlName];

    if (!elementRef) {
      return false;
    }

    const cursorPosition = elementRef.nativeElement.selectionStart;
    const prevValue = this.formGroup.get(controlName).value;
    const leftString = prevValue.substr(0, cursorPosition);
    const rightString = prevValue.substr(cursorPosition);

    this.formGroup
      .get(controlName)
      .setValue(leftString + linkContent + rightString);
  }
}
